import { useWatch, Control } from "react-hook-form";
import { useMemo } from "react";

/**
 * Custom hook to efficiently watch multiple form fields at once
 * This reduces the number of subscriptions and improves performance
 * 
 * @param control - The form control from react-hook-form
 * @param fields - Array of field names to watch
 * @returns An object with watched field values
 */
export function useFormWatch<T extends Record<string, any>>(
  control: Control<T>,
  fields: (keyof T)[]
) {
  // Watch all specified fields at once - useWatch returns an array when watching multiple fields
  const watchedValues = useWatch({
    control,
    name: fields as any,
  }) as any[];

  // Memoize the result to prevent unnecessary re-renders
  // Only recalculate when watchedValues array reference changes
  return useMemo(() => {
    const result: Partial<T> = {};
    fields.forEach((field, index) => {
      result[field] = watchedValues?.[index];
    });
    return result;
  }, [watchedValues, fields]);
}


